<template>
  <div
    class="pycad-vtk-frame"
    :class="{ 'pycad-frame--drawing': isDrawing }"
    tabindex="0"
    @pointerenter="hover = true"
    @pointerleave="hover = false"
    @focusin="hover = true"
    @focusout="hover = false"
  >
    <div class="pycad-vtk-stage" data-testid="two-view-container">
      <v-progress-linear
        v-if="isImageLoading"
        indeterminate
        class="pycad-loading"
        height="2"
        color="primary"
      />
      <div class="vtk-sub-container">
        <vtk-slice-view
          class="vtk-view"
          ref="vtkView"
          data-testid="vtk-view vtk-cine-view"
          :view-id="viewId"
          :image-id="currentImageID"
          :view-direction="VIEW_DIRECTION"
          :view-up="VIEW_UP"
        >
          <vtk-mouse-interaction-manipulator
            v-if="currentTool === Tools.Pan"
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 1 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 1, shift: true }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="vtkMouseCameraTrackballPanManipulator"
            :manipulator-props="{ button: 2 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            v-if="currentTool === Tools.Zoom"
            :manipulator-constructor="
              vtkMouseCameraTrackballZoomToMouseManipulator
            "
            :manipulator-props="{ button: 1 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-mouse-interaction-manipulator
            :manipulator-constructor="
              vtkMouseCameraTrackballZoomToMouseManipulator
            "
            :manipulator-props="{ button: 3 }"
          ></vtk-mouse-interaction-manipulator>
          <vtk-cine-scrub-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
          ></vtk-cine-scrub-manipulator>
          <vtk-cine-scrub-key-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
          ></vtk-cine-scrub-key-manipulator>
          <CineViewerOverlay :view-id="viewId" :image-id="currentImageID" />
          <vtk-base-slice-representation
            ref="baseSliceRep"
            :view-id="viewId"
            :image-id="currentImageID"
            :axis="VIEW_AXIS"
            :frame="currentFrame"
          ></vtk-base-slice-representation>
          <component
            v-for="(toolComponent, name) in annotationSliceTools"
            :key="name"
            :is="toolComponent"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="VIEW_DIRECTION"
          />
          <select-tool />
          <svg class="overlay-no-events">
            <bounding-rectangle :points="selectionPoints" />
          </svg>
          <slot></slot>
        </vtk-slice-view>
      </div>
    </div>

    <PycadSliceStrip
      v-if="hasFrameSlider"
      v-model="currentFrame"
      :min="frameRange[0]"
      :max="frameRange[1]"
      axis="FR"
      color="#ff9900"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import VtkSliceView from '@/src/components/vtk/VtkSliceView.vue';
import { VtkViewApi } from '@/src/types/vtk-types';
import { Tools } from '@/src/store/tools/types';
import VtkBaseSliceRepresentation from '@/src/components/vtk/VtkBaseSliceRepresentation.vue';
import { useViewAnimationListener } from '@/src/composables/useViewAnimationListener';
import SelectTool from '@/src/components/tools/SelectTool.vue';
import { enabledSliceTools } from '@/src/features/registry';
import { useDrawingToolActive } from '@/src/composables/annotationTool';
import {
  applyCameraOp,
  clearViewRotation,
  onCameraOp,
} from '@/src/core/cameraOps';
import BoundingRectangle from '@/src/components/tools/BoundingRectangle.vue';
import CineViewerOverlay from '@/src/components/CineViewerOverlay.vue';
import PycadSliceStrip from '@/src/components/pycad/PycadSliceStrip.vue';
import { useToolSelectionStore } from '@/src/store/tools/toolSelection';
import { useAnnotationToolStore, useToolStore } from '@/src/store/tools';
import { useWebGLWatchdog } from '@/src/composables/useWebGLWatchdog';
import { useCineFrame } from '@/src/composables/useCineFrame';
import VtkCineScrubManipulator from '@/src/components/vtk/VtkCineScrubManipulator.vue';
import VtkCineScrubKeyManipulator from '@/src/components/vtk/VtkCineScrubKeyManipulator.vue';
import VtkMouseInteractionManipulator from '@/src/components/vtk/VtkMouseInteractionManipulator.vue';
import vtkMouseCameraTrackballPanManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkMouseCameraTrackballZoomToMouseManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballZoomToMouseManipulator';
import { useResetViewsEvents } from '@/src/components/tools/ResetViews.vue';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { get2DViewingVectors } from '@/src/utils/getViewingVectors';
import type { LPSAxis } from '@/src/types/lps';

type Props = {
  viewId: string;
};

const VIEW_AXIS: LPSAxis = 'Axial';
const { viewDirection: VIEW_DIRECTION, viewUp: VIEW_UP } =
  get2DViewingVectors(VIEW_AXIS);

const vtkView = ref<VtkViewApi>();
const baseSliceRep = ref();

const props = defineProps<Props>();
const { viewId } = toRefs(props);

// Annotation tools that can be placed on cine frames. Crop and paint are
// excluded from cine views (CINE_DISALLOWED_TOOLS).
const annotationSliceTools = (() => {
  const { crop, paint, ...tools } = enabledSliceTools();
  void crop;
  void paint;
  return tools;
})();

const { currentImageID, currentImageData, isImageLoading } = useCurrentImage();

const hover = ref(false);

function resetCamera() {
  clearViewRotation(viewId.value);
  vtkView.value?.resetCamera();
}

useResetViewsEvents().onClick(resetCamera);

// rotate / flip requests from the toolbar, targeted at this view
onCameraOp((event) => {
  if (event.viewId !== viewId.value || !vtkView.value) return;
  applyCameraOp(vtkView.value, viewId.value, event.op, undefined, event.angle);
});

useWebGLWatchdog(vtkView);
useViewAnimationListener(vtkView, viewId, '2D');

const { currentTool } = storeToRefs(useToolStore());
const isDrawing = useDrawingToolActive();

const { frame: currentFrame, frameRange } = useCineFrame(
  viewId,
  currentImageID
);

const hasFrameSlider = computed(
  () => !!currentImageID.value && frameRange.value[1] > 0
);

onVTKEvent(currentImageData, 'onModified', () => {
  vtkView.value?.requestRender();
});

const selectionStore = useToolSelectionStore();
const selectionPoints = computed(() => {
  return selectionStore.selection
    .map((sel) => {
      const store = useAnnotationToolStore(sel.type);
      return { store, tool: store.toolByID[sel.id] };
    })
    .filter(
      ({ tool }) =>
        tool.imageID === currentImageID.value &&
        tool.frame === currentFrame.value &&
        !tool.hidden
    )
    .flatMap(({ store, tool }) => store.getPoints(tool.id));
});

// Touch baseSliceRep so its template ref participates in the component.
void baseSliceRep.value;
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
<style scoped src="@/src/components/pycad/pycad-vtk-view.css"></style>
